import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Copy, Users } from "lucide-react";
import { CodiceFiscale } from "codice-fiscale-js";
import api from "../lib/api";
import { Input } from "./ui/input";
import AddressAutocomplete from "./AddressAutocomplete";
import ComuneField from "./ComuneField";

const EMPTY = { cognome: "", nome: "", sesso: "", data_nascita: "", comune_nascita: "", codice_fiscale: "", via: "", civico: "", provincia: "", comune: "", cap: "", procura_firmata: false };
const inp = "h-9 w-full rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/40";

const F = ({ label, children, className = "" }) => (
  <label className={`block space-y-1 ${className}`}><span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
);

const fromAnag = (r) => ({
  cognome: r.cognome || "", nome: r.nome || "", sesso: r.sesso || "", data_nascita: (r.data_nascita || "").slice(0, 10),
  comune_nascita: r.luogo_nascita || "", codice_fiscale: (r.codice_fiscale || "").toUpperCase(),
  via: r.indirizzo || "", civico: r.numero_civico || r.civico || "", comune: r.comune || "", provincia: (r.provincia || "").toUpperCase(), cap: r.cap || "",
});

// Input con suggerimenti da Anagrafica (clienti e controparti).
const NameSuggest = ({ value, onChange, onPick, testId }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const q = (value || "").trim();
    if (!open || q.length < 2) { setList([]); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(async () => {
      const res = await Promise.all(["clienti", "controparti"].map((c) => api.get(`/anagrafiche/${c}?q=${encodeURIComponent(q)}`)
        .then(({ data }) => (data || []).filter((r) => r.cognome || r.nome).map((r) => ({ ...r, _cat: c }))).catch(() => [])));
      if (my === seq.current) setList(res.flat().slice(0, 10));
    }, 250);
    return () => clearTimeout(t);
  }, [value, open]);
  return (
    <div className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off" className={inp} data-testid={testId} />
      {open && list.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-xl" data-testid={`${testId}-suggestions`}>
          {list.map((r, i) => (
            <button key={`${r.id}-${i}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(fromAnag(r)); setOpen(false); }}
              data-testid={`${testId}-sugg-${i}`} className="block w-full px-3 py-1.5 text-left text-sm text-slate-900 hover:bg-amber-100">
              <span className="font-semibold text-slate-900">{[r.cognome, r.nome].filter(Boolean).join(" ")}</span>
              <span className="ml-2 text-[11px] text-slate-500">{[r.codice_fiscale, r.comune].filter(Boolean).join(" · ")}</span>
              <span className="ml-2 rounded bg-slate-200 px-1 text-[10px] uppercase text-slate-700">{r._cat === "clienti" ? "Cliente" : "Controparte"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const splitCivico = (s) => {
  const m = String(s || "").trim().match(/^(.*?)[,\s]+(\d+[A-Za-z]?(?:\/\w+)?)$/);
  return m ? { via: m[1], civico: m[2] } : { via: String(s || "").trim(), civico: "" };
};

const calcCF = (v) => {
  const [a, m, g] = String(v.data_nascita || "").split("-").map(Number);
  if (!v.cognome || !v.nome || !v.sesso || !v.comune_nascita || !(a && m && g)) return null;
  try {
    return new CodiceFiscale({ name: v.nome, surname: v.cognome, gender: v.sesso === "Femmina" ? "F" : "M", day: g, month: m, year: a, birthplace: v.comune_nascita }).toString();
  } catch (e) { return null; }
};

const ADDR_KEYS = ["via", "civico", "comune", "provincia", "cap"];
const pickAddr = (x) => (x && [x.via, x.comune, x.cap].some((s) => String(s || "").trim())
  ? Object.fromEntries(ADDR_KEYS.map((k) => [k, x[k] || ""])) : null);

const GenitoreForm = ({ n, v, set, addr, addrEp1 }) => {
  const t = (k) => `ep${n}-${k}`;
  const up = (k) => (e) => set({ ...v, [k]: e.target.value });
  const [cfManual, setCfManual] = useState(false);
  useEffect(() => {
    if (cfManual) return;
    const cf = calcCF(v);
    if (cf && cf !== v.codice_fiscale) set({ ...v, codice_fiscale: cf });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.cognome, v.nome, v.sesso, v.data_nascita, v.comune_nascita, cfManual]);
  const pick = (data) => { setCfManual(!!data.codice_fiscale); set({ ...v, ...data }); };
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-3" data-testid={`ep${n}-form`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-amber-300">Esercente potestà {n}</div>
        <div className="flex flex-wrap justify-end gap-1.5">
        <button type="button" disabled={!addr} onClick={() => set({ ...v, ...addr })} data-testid={t("copia-indirizzo")}
          title={addr ? "Copia via, civico, comune, provincia e CAP del minore" : "Nessun indirizzo inserito per il minore"}
          className="flex items-center gap-1 rounded-md border border-amber-500/40 px-2 py-1 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-40">
          <Copy className="h-3.5 w-3.5" />Copia indirizzo del minore
        </button>
        {n === 2 && (
          <button type="button" disabled={!addrEp1} onClick={() => set({ ...v, ...addrEp1 })} data-testid="ep2-copia-indirizzo-ep1"
            title={addrEp1 ? "Copia via, civico, comune, provincia e CAP del primo genitore" : "Nessun indirizzo inserito per il primo genitore"}
            className="flex items-center gap-1 rounded-md border border-amber-500/40 px-2 py-1 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-40">
            <Copy className="h-3.5 w-3.5" />Copia dal genitore 1
          </button>
        )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Cognome"><NameSuggest value={v.cognome} onChange={(x) => set({ ...v, cognome: x })} onPick={pick} testId={t("cognome")} /></F>
        <F label="Nome"><NameSuggest value={v.nome} onChange={(x) => set({ ...v, nome: x })} onPick={pick} testId={t("nome")} /></F>
        <F label="Sesso">
          <select value={v.sesso} onChange={up("sesso")} className={inp} data-testid={t("sesso")}>
            <option value="">—</option><option value="Maschio">Maschio</option><option value="Femmina">Femmina</option>
          </select>
        </F>
        <F label="Data di nascita"><Input type="date" value={v.data_nascita} onChange={up("data_nascita")} className={inp} data-testid={t("data-nascita")} /></F>
        <F label="Comune di nascita"><input value={v.comune_nascita} onChange={up("comune_nascita")} className={inp} data-testid={t("comune-nascita")} /></F>
        <F label="Codice fiscale"><input value={v.codice_fiscale} onChange={(e) => { setCfManual(true); set({ ...v, codice_fiscale: e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 16) }); }} className={`${inp} font-mono`} data-testid={t("cf")} /></F>
        <div className="col-span-2 grid grid-cols-[1fr_7rem] gap-3">
          <F label="Via">
            <AddressAutocomplete value={v.via} onChange={(x) => set({ ...v, via: x })} city={v.comune} prov={v.provincia} testId={t("via")} inputClassName={inp}
              onPick={(p) => { const s = splitCivico(p.indirizzo); set({ ...v, via: s.via, civico: s.civico || v.civico, comune: p.comune || v.comune, provincia: (p.provincia || v.provincia || "").toUpperCase(), cap: p.cap || v.cap }); }} />
          </F>
          <F label="N° Civico"><input value={v.civico} onChange={up("civico")} className={inp} data-testid={t("civico")} /></F>
        </div>
        <F label="Comune">
          <ComuneField value={v.comune} onChange={(x) => set({ ...v, comune: x })} className={inp} testId={t("comune")}
            onPick={(c) => set({ ...v, comune: c.nome, provincia: c.provincia || "", cap: c.cap || "" })} />
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Provincia"><input value={v.provincia} onChange={(e) => set({ ...v, provincia: e.target.value.toUpperCase().slice(0, 2) })} className={inp} data-testid={t("provincia")} /></F>
          <F label="CAP"><input value={v.cap} onChange={(e) => set({ ...v, cap: e.target.value.replace(/\D/g, "").slice(0, 5) })} className={inp} data-testid={t("cap")} /></F>
        </div>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-200">
          <input type="checkbox" checked={!!v.procura_firmata} onChange={(e) => set({ ...v, procura_firmata: e.target.checked })} className="h-4 w-4 accent-emerald-500" data-testid={t("procura")} />
          Procura firmata
        </label>
      </div>
    </div>
  );
};

// Sezione espandibile con i dati dei due esercenti la potestà (cliente minore).
export const EsercentiPotestaSection = ({ value, onChange, indirizzoMinore }) => {
  const m = indirizzoMinore || {};
  const addr = [m.via, m.comune, m.cap].some((x) => String(x || "").trim())
    ? { via: m.via || "", civico: m.civico || "", comune: m.comune || "", provincia: (m.provincia || "").toUpperCase(), cap: m.cap || "" } : null;
  const [open, setOpen] = useState(true);
  const eps = [0, 1].map((i) => ({ ...EMPTY, ...((value || [])[i] || {}) }));
  const filled = eps.filter((x) => x.cognome || x.nome).length;
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5" data-testid="esercenti-potesta-section">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="esercenti-potesta-toggle"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold uppercase tracking-wide text-amber-300">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Users className="h-4 w-4" />Esercenti Potestà{filled ? ` (${filled})` : ""}
      </button>
      {open && (
        <div className="space-y-3 px-3 pb-3">
          {eps.map((v, i) => <GenitoreForm key={i} n={i + 1} v={v} addr={addr} addrEp1={pickAddr(eps[0])} set={(nv) => onChange(eps.map((x, j) => (j === i ? nv : x)))} />)}
        </div>
      )}
    </div>
  );
};
